import React, { useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Close,
  CheckCircle,
  Cancel,
  FilterAltOff,
  Check,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { setLabelFilters } from "../../../redux/actions/user";
import { DEFAULT_LABEL_COLOR } from "../../../constants/pickerColors";

const TaskFilterDialog = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const labels = useSelector((state) => state.user.labels);
  const labelFilters = useSelector((state) => state.user.filters?.labels || {});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleFilterChange = useCallback(
    (labelId, filterType, status) => {
      const currentFilter = labelFilters[labelId] || {
        include: false,
        exclude: false,
      };

      // If setting include, clear exclude and vice versa
      const newFilter = {
        include:
          filterType === "include"
            ? status
            : filterType === "exclude" && status
              ? false
              : currentFilter.include,
        exclude:
          filterType === "exclude"
            ? status
            : filterType === "include" && status
              ? false
              : currentFilter.exclude,
      };

      dispatch(
        setLabelFilters({
          ...labelFilters,
          [labelId]: newFilter,
        }),
      );
    },
    [dispatch, labelFilters],
  );

  const clearAllFilters = useCallback(() => {
    const clearedFilters = {};
    Object.keys(labels).forEach((labelId) => {
      clearedFilters[labelId] = { include: false, exclude: false };
    });
    dispatch(setLabelFilters(clearedFilters));
  }, [dispatch, labels]);

  const activeFilterCount = Object.values(labelFilters).filter(
    (f) => f?.include || f?.exclude,
  ).length;

  const labelArray = Object.values(labels || {});

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight="bold">
            Filter Tasks
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Show or hide tasks by label
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          sx={{ color: "grey.500" }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Active Filters Summary */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: activeFilterCount > 0 ? "#004a3c" : "grey.200", // $primary-dark
            color: activeFilterCount > 0 ? "white !important" : "text.primary",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            transition: "all 0.3s ease",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                mb: "0 !important",
                color:
                  activeFilterCount > 0 ? "white !important" : "text.primary",
                transition: "color 0.3s ease",
              }}
            >
              {activeFilterCount > 0
                ? `${activeFilterCount} filter${activeFilterCount > 1 ? "s" : ""} active`
                : "No filters active"}
            </Typography>
          </Box>
          {activeFilterCount > 0 && (
            <Button
              size="small"
              startIcon={<FilterAltOff />}
              onClick={clearAllFilters}
              sx={{
                color: "white !important",
                "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
              }}
            >
              Clear All
            </Button>
          )}
        </Box>

        {/* Legend */}
        <Box
          sx={{
            px: 2,
            py: 1,
            bgcolor: "grey.50",
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <CheckCircle sx={{ fontSize: 18, color: "#197b68" }} />
            <Typography variant="caption" color="text.secondary">
              Include: Show only tasks with this label
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Cancel sx={{ fontSize: 18, color: "#cb5538" }} />
            <Typography variant="caption" color="text.secondary">
              Exclude: Hide tasks with this label
            </Typography>
          </Box>
        </Box>

        {labelArray.length > 0 ? (
          <List
            className="scroller"
            sx={{ py: 0, maxHeight: "60vh", overflow: "auto" }}
          >
            {labelArray.map((label) => {
              const filter = labelFilters[label.labelId] || {
                include: false,
                exclude: false,
              };
              const labelColor = label.color || DEFAULT_LABEL_COLOR;

              return (
                <ListItem
                  key={label.labelId}
                  sx={{
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    py: isMobile ? 1 : 1.5,
                    px: isMobile ? 1 : 2,
                    display: "flex",
                    flexDirection: "row",
                    gap: isMobile ? 1 : 0,
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: isMobile ? 1 : 1.5,
                      flex: "0 1 auto",
                      minWidth: isMobile ? "90px" : "150px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Box
                      sx={{
                        width: isMobile ? 12 : 14,
                        height: isMobile ? 12 : 14,
                        borderRadius: "50%",
                        bgcolor: labelColor,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant={isMobile ? "body2" : "body1"}
                      fontWeight={
                        filter.include || filter.exclude ? "bold" : "normal"
                      }
                      sx={{
                        mb: "0 !important",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {label.name}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flex: 1,
                      ml: isMobile ? 0 : 2,
                      minWidth: "50%",
                    }}
                  >
                    <Chip
                      icon={
                        filter.include ? (
                          <Check
                            sx={{
                              fontSize: isMobile ? 16 : 18,
                              color: "white !important",
                            }}
                          />
                        ) : undefined
                      }
                      label="Include"
                      size={isMobile ? "small" : "medium"}
                      variant={filter.include ? "filled" : "outlined"}
                      sx={{
                        flex: 1,
                        flexShrink: 0,
                        minWidth: 0,
                        py: isMobile ? 2 : 2,
                        "& .MuiChip-label": {
                          fontSize: isMobile ? "0.75rem" : "0.85rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        ...(filter.include && {
                          bgcolor: "#197b68",
                          color: "white !important",
                          borderColor: "#197b68",
                          "& .MuiChip-icon": { color: "white !important" },
                        }),
                        ...(!filter.include && {
                          borderColor: "#197b68",
                          color: "#197b68 !important",
                          "& .MuiChip-label": {
                            color: "#197b68 !important",
                          },
                        }),
                      }}
                      onClick={() =>
                        handleFilterChange(
                          label.labelId,
                          "include",
                          !filter.include,
                        )
                      }
                    />
                    <Chip
                      icon={
                        filter.exclude ? (
                          <Check
                            sx={{
                              fontSize: isMobile ? 16 : 18,
                              color: "white !important",
                            }}
                          />
                        ) : undefined
                      }
                      label="Exclude"
                      size={isMobile ? "small" : "medium"}
                      variant={filter.exclude ? "filled" : "outlined"}
                      sx={{
                        flex: 1,
                        flexShrink: 0,
                        minWidth: 0,
                        py: isMobile ? 2 : 2,
                        "& .MuiChip-label": {
                          fontSize: isMobile ? "0.75rem" : "0.85rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        ...(filter.exclude && {
                          bgcolor: "#cb5538",
                          color: "white !important",
                          borderColor: "#cb5538",
                          "& .MuiChip-icon": { color: "white !important" },
                        }),
                        ...(!filter.exclude && {
                          borderColor: "#cb5538",
                          color: "#cb5538 !important",
                          "& .MuiChip-label": {
                            color: "#cb5538 !important",
                          },
                        }),
                      }}
                      onClick={() =>
                        handleFilterChange(
                          label.labelId,
                          "exclude",
                          !filter.exclude,
                        )
                      }
                    />
                  </Box>
                </ListItem>
              );
            })}
          </List>
        ) : (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No labels yet. Create labels to start filtering tasks.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskFilterDialog;
